import type { ReactElement, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import type { LibraryItemSummary, UserCollectionItemSummary, UserCollectionSummary } from '@fetmode/types';
import { isPrivateCaptureMediaKind } from '@fetmode/types';
import { t } from '@fetmode/i18n';
import { formatUsername } from '@fetmode/utils';
import { Button } from '@fetmode/ui';
import { Link } from 'react-router-dom';

import { useAuth } from '../auth/index.js';
import {
  COMPACT_GRID_PAGE_SIZE,
  ThumbGridPager,
  usePagedItems,
} from '../../shared/components/ThumbGridPager/index.js';

export function LibraryPage(): ReactElement {
  const { api } = useAuth();
  const [library, setLibrary] = useState<LibraryItemSummary[]>([]);
  const [collections, setCollections] = useState<UserCollectionSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = (): void => {
    void Promise.all([api.listLibrary(), api.listCollections()])
      .then(([libraryResult, collectionResult]) => {
        setLibrary(libraryResult.library);
        setCollections(collectionResult.collections);
      })
      .catch(() => {
        setError(t('common.library.loadError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load on mount with current api
  }, [api]);

  const purchases = library.filter((entry) => !isPrivateCaptureMediaKind(entry.item.kind));
  const captures = library.filter((entry) => isPrivateCaptureMediaKind(entry.item.kind));
  const savedShows = collections.flatMap((collection) =>
    collection.items.filter((item) => item.kind === 'SHOW'),
  );
  const empty = purchases.length === 0 && captures.length === 0 && savedShows.length === 0;

  const handleRemove = (itemId: string): void => {
    void api
      .removeCollectionItem(itemId)
      .then(() => {
        setCollections((current) =>
          current.map((collection) => ({
            ...collection,
            items: collection.items.filter((item) => item.id !== itemId),
          })),
        );
      })
      .catch(() => {
        setError(t('common.library.saveVideoError'));
      });
  };

  return (
    <section>
      {isLoading && (
        <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.library.loading')}</p>
      )}
      {error && <p className="fm-error">{error}</p>}

      {!isLoading && empty && (
        <div className="fm-card">
          <p style={{ margin: 0 }}>{t('common.library.empty')}</p>
          <p style={{ margin: '0.5rem 0 0', color: 'var(--fm-color-text-muted)' }}>
            {t('common.library.emptyHint')}
          </p>
          <Link to="/videos" style={{ display: 'inline-block', marginTop: '1rem' }}>
            <Button label={t('common.library.browseVideos')} />
          </Link>
        </div>
      )}

      <PagedLibraryMediaSection
        heading={t('common.library.sectionPurchases')}
        items={purchases}
        render={(entry) => <LibraryMediaCard key={entry.purchaseId} entry={entry} />}
      />
      <PagedLibraryMediaSection
        heading={t('common.library.sectionCaptures')}
        items={captures}
        render={(entry) => <LibraryMediaCard key={entry.purchaseId} entry={entry} />}
      />
      <PagedLibrarySavedSection
        heading={t('common.library.sectionSavedShows')}
        items={savedShows}
        onRemove={handleRemove}
      />
    </section>
  );
}

function PagedLibraryMediaSection({
  heading,
  items,
  render,
}: {
  heading: string;
  items: readonly LibraryItemSummary[];
  render: (entry: LibraryItemSummary) => ReactNode;
}): ReactElement | null {
  const paging = usePagedItems(items, COMPACT_GRID_PAGE_SIZE);
  if (items.length === 0) {
    return null;
  }
  return (
    <LibrarySection
      heading={heading}
      page={paging.page}
      total={paging.total}
      onPageChange={paging.setPage}
    >
      {paging.pageItems.map(render)}
    </LibrarySection>
  );
}

function PagedLibrarySavedSection({
  heading,
  items,
  onRemove,
}: {
  heading: string;
  items: readonly UserCollectionItemSummary[];
  onRemove: (itemId: string) => void;
}): ReactElement | null {
  const paging = usePagedItems(items, COMPACT_GRID_PAGE_SIZE);
  if (items.length === 0) {
    return null;
  }
  return (
    <LibrarySection
      heading={heading}
      page={paging.page}
      total={paging.total}
      onPageChange={paging.setPage}
    >
      {paging.pageItems.map((item) => (
        <SavedShowCard key={item.id} item={item} onRemove={onRemove} />
      ))}
    </LibrarySection>
  );
}

function LibrarySection({
  heading,
  children,
  page,
  total,
  onPageChange,
}: {
  heading: string;
  children: ReactNode;
  page: number;
  total: number;
  onPageChange: (page: number) => void;
}): ReactElement {
  return (
    <section style={{ marginTop: '1.5rem' }}>
      <h2 style={{ margin: 0, fontFamily: 'var(--fm-font-heading)', fontSize: '1.15rem' }}>
        {heading}
      </h2>
      <ul
        style={{
          listStyle: 'none',
          margin: '1rem 0 0',
          padding: 0,
          display: 'grid',
          gap: '1rem',
        }}
      >
        {children}
      </ul>
      <ThumbGridPager
        page={page}
        total={total}
        pageSize={COMPACT_GRID_PAGE_SIZE}
        label={heading}
        onPageChange={onPageChange}
      />
    </section>
  );
}

function LibraryMediaCard({ entry }: { entry: LibraryItemSummary }): ReactElement {
  const isImage = entry.item.kind === 'SHOW_SNAPSHOT';
  return (
    <li>
      <article className="fm-card" style={{ marginTop: 0 }}>
        <h3 style={{ margin: 0, fontFamily: 'var(--fm-font-heading)' }}>{entry.item.title}</h3>
        <p style={{ margin: '0.25rem 0 0', color: 'var(--fm-color-text-muted)' }}>
          {formatUsername(entry.item.model.username)} · {entry.item.model.displayName}
        </p>
        {isImage ? (
          <img
            src={entry.item.mediaUrl}
            alt=""
            style={{ width: '100%', marginTop: '0.75rem', borderRadius: '0.5rem' }}
          />
        ) : (
          <video
            controls
            src={entry.item.mediaUrl}
            style={{ width: '100%', marginTop: '0.75rem', borderRadius: '0.5rem' }}
          />
        )}
        <p style={{ margin: '0.75rem 0 0', color: 'var(--fm-color-text-muted)' }}>
          {t('common.library.purchasedOn').replace(
            '{{date}}',
            new Date(entry.purchasedAt).toLocaleDateString(),
          )}
        </p>
      </article>
    </li>
  );
}

function SavedShowCard({
  item,
  onRemove,
}: {
  item: UserCollectionItemSummary;
  onRemove: (itemId: string) => void;
}): ReactElement {
  const live = item.show?.status === 'LIVE' || item.show?.status === 'PAUSED' || item.show?.status === 'AWAY';
  return (
    <li>
      <article className="fm-card" style={{ marginTop: 0 }}>
        <h3 style={{ margin: 0, fontFamily: 'var(--fm-font-heading)' }}>
          {item.model.displayName}
        </h3>
        <p style={{ margin: '0.25rem 0 0', color: 'var(--fm-color-text-muted)' }}>
          {formatUsername(item.model.username)}
          {' · '}
          {live ? t('common.library.savedShowLive') : t('common.library.savedShowEnded')}
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
          <Link to={`/@${item.model.username}`}>
            <Button label={t('common.library.openShow')} />
          </Link>
          <Button
            label={t('common.library.removeSaved')}
            variant="ghost"
            onClick={() => {
              onRemove(item.id);
            }}
          />
        </div>
      </article>
    </li>
  );
}
